import { createContext, forwardRef, useContext, type HTMLAttributes, type TdHTMLAttributes, type ThHTMLAttributes } from "react";
import { cn } from "@/lib/cn";
import type { Scale } from "./tokens";

/**
 * Compound `Table` (`Table`/`Thead`/`Tbody`/`Tr`/`Th`/`Td`) · reemplaza el
 * `<Table>` de Mantine, usado en 31 archivos SIN un wrapper compartido hoy
 * (cada feature repite `Table.Thead/Tbody/Tr/Th/Td` desde cero). A
 * diferencia de Mantine (que expone `verticalSpacing`/`highlightOnHover`
 * como CSS vars internas), acá se resuelven vía un `Context` liviano que
 * `Tr`/`Th`/`Td` consumen — mismo resultado, sin inventar una capa de
 * CSS vars propia.
 *
 * NO incluye el wrapper de scroll horizontal (`overflow-x: auto`) —
 * tampoco lo hacía Mantine · cada tabla ancha se envuelve en su propio
 * `<div className="overflow-x-auto">` en el call-site (convención de
 * `AGENTS.md`: "Wide content... overflow-x: auto en su propio contenedor").
 */

interface TableConfig {
  vSpacing: Scale;
  hSpacing: Scale;
  highlightOnHover: boolean;
  withRowBorders: boolean;
}

const TableContext = createContext<TableConfig>({
  vSpacing: "sm",
  hSpacing: "sm",
  highlightOnHover: false,
  withRowBorders: true,
});

const V_PADDING = { xs: "py-1", sm: "py-2", md: "py-3", lg: "py-4", xl: "py-5" } as const;
const H_PADDING = { xs: "px-1.5", sm: "px-2.5", md: "px-3.5", lg: "px-4", xl: "px-5" } as const;

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  verticalSpacing?: Scale;
  horizontalSpacing?: Scale;
  layout?: "auto" | "fixed";
  highlightOnHover?: boolean;
  withRowBorders?: boolean;
  /** min-width en px · fuerza el ancho mínimo de la tabla (para scroll horizontal en tablas anchas). */
  minWidth?: number;
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      verticalSpacing = "sm",
      horizontalSpacing = "sm",
      layout = "auto",
      highlightOnHover = false,
      withRowBorders = true,
      minWidth,
      className,
      style,
      ...props
    },
    ref
  ) => (
    <TableContext.Provider value={{ vSpacing: verticalSpacing, hSpacing: horizontalSpacing, highlightOnHover, withRowBorders }}>
      <table
        ref={ref}
        className={cn("w-full text-sm", layout === "fixed" && "table-fixed", className)}
        style={{ minWidth, ...style }}
        {...props}
      />
    </TableContext.Provider>
  )
);
Table.displayName = "Table";

export function Thead({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  const config = useContext(TableContext);
  return (
    // El resaltado de hover (`highlightOnHover`) es para filas de DATOS ·
    // dentro del thead se apaga (los `Tr` de encabezado/filtros no deben
    // aclararse al pasar el mouse).
    <TableContext.Provider value={{ ...config, highlightOnHover: false }}>
      <thead className={cn("border-b border-border text-left text-xs font-semibold text-muted-foreground", className)} {...props} />
    </TableContext.Provider>
  );
}

export function Tbody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
  return <tbody className={className} {...props} />;
}

export const Tr = forwardRef<HTMLTableRowElement, HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const { highlightOnHover, withRowBorders } = useContext(TableContext);
    return (
      <tr
        ref={ref}
        className={cn(withRowBorders && "border-b border-border last:border-0", highlightOnHover && "hover:bg-muted/50 transition-colors", className)}
        {...props}
      />
    );
  }
);
Tr.displayName = "Tr";

export interface ThProps extends ThHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
}

export function Th({ align = "left", className, style, ...props }: ThProps) {
  const { vSpacing, hSpacing } = useContext(TableContext);
  return (
    <th
      className={cn(
        "align-top font-semibold",
        V_PADDING[vSpacing],
        H_PADDING[hSpacing],
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className
      )}
      style={style}
      {...props}
    />
  );
}

export interface TdProps extends TdHTMLAttributes<HTMLTableCellElement> {
  align?: "left" | "right" | "center";
}

export function Td({ align = "left", className, style, ...props }: TdProps) {
  const { vSpacing, hSpacing } = useContext(TableContext);
  return (
    <td
      className={cn(
        V_PADDING[vSpacing],
        H_PADDING[hSpacing],
        align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left",
        className
      )}
      style={style}
      {...props}
    />
  );
}
